import {
    ALMETLAA_ACTION,
    ALMETLAA_SCREEN,
    DT_PAYLOAD,
} from '@/core/constants/dynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';

export type ProjectPromoBannerKind = 'master_plan' | 'explore_vr';

export type CatalogEventType = 'view' | 'action' | 'error';

export interface TrackCatalogEventInput {
    screen: string;
    actionName: string;
    eventType: CatalogEventType;
    properties?: Record<string, string | number | boolean | undefined>;
}

/** Track using catalogue `p_screen` + `p_actionName` (web + native). */
export function trackCatalogEvent(input: TrackCatalogEventInput): void {
    const { screen, actionName, eventType, properties } = input;
    trackDynatraceEvent({
        p_screen: screen,
        p_actionName: actionName,
        eventType,
        ...properties,
    });
}

const recentCatalogKeys = new Map<string, number>();

function trackCatalogEventDeduped(input: TrackCatalogEventInput, windowMs: number): void {
    const unitId = input.properties?.unitId;
    const key = `${input.screen}|${input.actionName}|${unitId ?? ''}`;
    const now = Date.now();
    const last = recentCatalogKeys.get(key) ?? 0;
    if (now - last < windowMs) {
        return;
    }
    recentCatalogKeys.set(key, now);
    trackCatalogEvent(input);
}

export function trackAdhaReservationCardClick(unitId?: string): void {
    const id = unitId?.trim();
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.CATALOGUE,
        actionName: ALMETLAA_ACTION.CATALOGUE.RESERVATION_CARD_CLICK,
        eventType: 'action',
        ...(id ? { properties: { unitId: id } } : {}),
    });
}

export function trackAdhaAppointmentCardClick(unitId?: string): void {
    const id = unitId?.trim();
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.CATALOGUE,
        actionName: ALMETLAA_ACTION.CATALOGUE.APPOINTMENT_CARD_CLICK,
        eventType: 'action',
        ...(id ? { properties: { unitId: id } } : {}),
    });
}

/** Reservation summary sheet. Deduped so the card click and the sheet mount send it once. */
export function trackReservationSummaryOpen(unitId?: string): void {
    const id = unitId?.trim();
    trackCatalogEventDeduped({
        screen: ALMETLAA_SCREEN.UNIT,
        actionName: ALMETLAA_ACTION.RESERVE.RESERVATION_SUMMARY_OPEN,
        eventType: 'action',
        ...(id ? { properties: { unitId: id } } : {}),
    }, 1200);
}

/** Confirm-reservation overlay (web dialog and native reserve screen). */
export function trackConfirmReservationDialogView(unitId?: string): void {
    const id = unitId?.trim();
    trackCatalogEventDeduped({
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: ALMETLAA_ACTION.RESERVE.CONFIRM_DIALOG_VIEW,
        eventType: 'view',
        ...(id ? { properties: { unitId: id } } : {}),
    }, 1200);
}

export interface LandingSpaceDetailPayload {
    spaceKey: string;
    spaceName?: string | null;
    experienceName?: string | null;
    locale?: string | null;
}

export function landingSpaceDetailProperties(
    payload: LandingSpaceDetailPayload,
): Record<string, string | undefined> {
    return {
        [DT_PAYLOAD.SPACE_KEY]: payload.spaceKey,
        [DT_PAYLOAD.SPACE_NAME]: payload.spaceName?.trim() || undefined,
        [DT_PAYLOAD.EXPERIENCE_NAME]: payload.experienceName?.trim() || undefined,
        [DT_PAYLOAD.LOCALE]: payload.locale?.trim() || undefined,
    };
}

export interface GuestProjectDetailPayload {
    projectId: string;
    projectName?: string | null;
    location?: string | null;
    locale?: string | null;
}

export function guestProjectDetailProperties(
    payload: GuestProjectDetailPayload,
): Record<string, string | undefined> {
    return {
        [DT_PAYLOAD.PROJECT_ID]: payload.projectId,
        [DT_PAYLOAD.PROJECT_NAME]: payload.projectName?.trim() || undefined,
        [DT_PAYLOAD.LOCATION]: payload.location?.trim() || undefined,
        [DT_PAYLOAD.LOCALE]: payload.locale?.trim() || undefined,
    };
}

export interface AdhaUnitDetailPayload {
    unitId: string;
    unitName?: string | null;
    projectId?: string | null;
    projectName?: string | null;
    locale?: string | null;
}

export function adhaUnitDetailProperties(
    payload: AdhaUnitDetailPayload,
): Record<string, string | undefined> {
    return {
        [DT_PAYLOAD.UNIT_ID]: payload.unitId,
        [DT_PAYLOAD.UNIT_NAME]: payload.unitName?.trim() || undefined,
        [DT_PAYLOAD.PROJECT_ID]: payload.projectId?.trim() || undefined,
        [DT_PAYLOAD.PROJECT_NAME]: payload.projectName?.trim() || undefined,
        [DT_PAYLOAD.LOCALE]: payload.locale?.trim() || undefined,
    };
}

export interface AdhaProjectDetailPayload {
    projectId: string;
    projectName?: string | null;
    locale?: string | null;
}

export function adhaProjectDetailProperties(
    payload: AdhaProjectDetailPayload,
): Record<string, string | undefined> {
    return {
        [DT_PAYLOAD.PROJECT_ID]: payload.projectId,
        [DT_PAYLOAD.PROJECT_NAME]: payload.projectName?.trim() || undefined,
        [DT_PAYLOAD.LOCALE]: payload.locale?.trim() || undefined,
    };
}

export interface PaymentDetailPayload {
    paymentId?: string | null;
    propertyId?: string | null;
    propertyName?: string | null;
    orderRef?: string | null;
}

export function paymentDetailProperties(
    payload: PaymentDetailPayload,
): Record<string, string | undefined> {
    return {
        paymentId: payload.paymentId?.trim() || undefined,
        [DT_PAYLOAD.PROPERTY_ID]: payload.propertyId?.trim() || undefined,
        [DT_PAYLOAD.PROPERTY_NAME]: payload.propertyName?.trim() || undefined,
        [DT_PAYLOAD.ORDER_REF]: payload.orderRef?.trim() || undefined,
    };
}

/** Master Plan / Explore VR promo banners (web ADHA listing + native project detail). */
export function trackProjectPromoBannerClick(input: {
    projectId: string;
    projectName?: string | null;
    kind: ProjectPromoBannerKind;
}): void {
    const projectId = input.projectId.trim();
    if (!projectId) {
        return;
    }
    const actionName =
        input.kind === 'master_plan'
            ? ALMETLAA_ACTION.PROJECT.MASTER_PLAN_BANNER_CLICK
            : ALMETLAA_ACTION.PROJECT.EXPLORE_VR_BANNER_CLICK;
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.PROJECT,
        actionName,
        eventType: 'action',
        properties: adhaProjectDetailProperties({
            projectId,
            projectName: input.projectName ?? null,
        }),
    });
}

// --- Emirates ID / Almetlaa auth (catalogue gaps only; legacy login events unchanged) ---

export type EmiratesIdLoginStepKey = 'EMIRATES_ID' | 'MOBILE_OTP' | 'EMAIL_OTP';

const EMIRATES_ID_STEP_VIEW_ACTION: Record<EmiratesIdLoginStepKey, string> = {
    EMIRATES_ID: ALMETLAA_ACTION.AUTH.LOGIN_EMIRATES_ID_VIEW,
    MOBILE_OTP: ALMETLAA_ACTION.AUTH.MOBILE_OTP_VIEW,
    EMAIL_OTP: ALMETLAA_ACTION.AUTH.EMAIL_OTP_VIEW,
};

export function trackEmiratesIdLoginStepView(step: EmiratesIdLoginStepKey): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: EMIRATES_ID_STEP_VIEW_ACTION[step],
        eventType: 'view',
    });
}

export function trackEmiratesIdLoginStartClick(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.LOGIN_EMIRATES_ID_START_CLICK,
        eventType: 'action',
    });
}

export function trackEmiratesIdSubmitClick(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.LOGIN_EMIRATES_ID_SUBMIT_CLICK,
        eventType: 'action',
    });
}

export function trackEmiratesIdSubmitError(message: string): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.LOGIN_EMIRATES_ID_ERROR,
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackEmiratesIdMobileOtpVerifyClick(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.MOBILE_OTP_VERIFY_CLICK,
        eventType: 'action',
    });
}

export function trackEmiratesIdEmailOtpVerifyClick(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.EMAIL_OTP_VERIFY_CLICK,
        eventType: 'action',
    });
}

export function trackEmiratesIdMobileOtpVerifySuccess(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.MOBILE_OTP_VERIFY_SUCCESS,
        eventType: 'action',
    });
}

export function trackEmiratesIdMobileOtpVerifyError(message: string): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.MOBILE_OTP_VERIFY_ERROR,
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackEmiratesIdEmailOtpVerifySuccess(): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.EMAIL_OTP_VERIFY_SUCCESS,
        eventType: 'action',
    });
}

export function trackEmiratesIdEmailOtpVerifyError(message: string): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.EMAIL_OTP_VERIFY_ERROR,
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackEmiratesIdOtpResendClick(channel: 'mobile' | 'email'): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName:
            channel === 'mobile'
                ? ALMETLAA_ACTION.AUTH.MOBILE_OTP_RESEND_CLICK
                : ALMETLAA_ACTION.AUTH.EMAIL_OTP_RESEND_CLICK,
        eventType: 'action',
    });
}

export function trackEmiratesIdOtpResendError(channel: 'mobile' | 'email', message: string): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName:
            channel === 'mobile'
                ? ALMETLAA_ACTION.AUTH.MOBILE_OTP_RESEND_ERROR
                : ALMETLAA_ACTION.AUTH.EMAIL_OTP_RESEND_ERROR,
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackEmiratesIdRaiseSupportClick(step: 'MOBILE_OTP' | 'EMAIL_OTP'): void {
    trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: ALMETLAA_ACTION.AUTH.LOGIN_OTP_RAISE_SUPPORT_CLICK,
        eventType: 'action',
        properties: { otpStep: step === 'EMAIL_OTP' ? 'email' : 'mobile' },
    });
}

// --- Guest project detail actions ---

function guestProjectCatalogAction(
    actionName: string,
    projectId: string,
    extra?: Record<string, string | undefined>,
): void {
    if (!projectId.trim()) {
        return;
    }
    trackCatalogEvent({
        screen: DT_SCREEN.GUEST,
        actionName,
        eventType: 'action',
        properties: { projectId: projectId.trim(), ...extra },
    });
}

export function trackGuestLanguageChange(locale: string, projectId?: string): void {
    const nextLocale = locale.trim();
    if (!nextLocale) {
        return;
    }
    trackCatalogEvent({
        screen: DT_SCREEN.GUEST,
        actionName: 'guest_language_change',
        eventType: 'action',
        properties: {
            locale: nextLocale,
            ...(projectId?.trim() ? { projectId: projectId.trim() } : {}),
        },
    });
}

export function trackGuestLoginOverlayClick(projectId?: string): void {
    trackCatalogEvent({
        screen: DT_SCREEN.GUEST,
        actionName: 'guest_login_overlay_click',
        eventType: 'action',
        ...(projectId?.trim() ? { properties: { projectId: projectId.trim() } } : {}),
    });
}

export function trackGuestLoginPromptMaybeLaterClick(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.GUEST,
        actionName: 'guest_login_prompt_maybe_later_click',
        eventType: 'action',
    });
}

export function trackGuestProjectCarouselManualChange(projectId: string, imageIndex: number): void {
    if (!projectId.trim() || !Number.isFinite(imageIndex)) {
        return;
    }
    trackCatalogEvent({
        screen: DT_SCREEN.GUEST,
        actionName: 'guest_project_carousel_manual_change',
        eventType: 'action',
        properties: { projectId: projectId.trim(), imageIndex },
    });
}

export function trackGuestProjectBrochureDownloadClick(projectId: string): void {
    guestProjectCatalogAction('guest_project_brochure_download_click', projectId);
}

export function trackGuestProjectMasterPlanViewClick(projectId: string): void {
    guestProjectCatalogAction('guest_project_master_plan_view_click', projectId);
}

export function trackGuestProjectMapOpenClick(projectId: string): void {
    guestProjectCatalogAction('guest_project_map_open_click', projectId);
}

export function trackGuestProjectDetailTabClick(
    projectId: string,
    tab: 'details' | 'explore',
): void {
    guestProjectCatalogAction('guest_project_detail_tab_click', projectId, { tab });
}

// --- Web profile dropdown (catalogue gaps; mobile ProfileScreen uses legacy) ---

export function trackProfileLogoutPress(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_logout_press',
        eventType: 'action',
    });
}

export function trackProfileLogoutConfirm(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_logout_confirm',
        eventType: 'action',
    });
}

export function trackProfileLogoutSuccess(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_logout_success',
        eventType: 'action',
    });
}

export function trackProfileLogoutError(message: string): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_logout_error',
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackProfileOpenPersonalDetails(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_personal_details',
        eventType: 'action',
    });
}

export function trackProfileOpenLoanDetails(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_loan_details',
        eventType: 'action',
    });
}

export function trackProfileOpenIdProof(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_id_proof',
        eventType: 'action',
    });
}

export function trackProfileOpenPrivacySecurity(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_privacy_security',
        eventType: 'action',
    });
}

export function trackProfileOpenFaq(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_faq',
        eventType: 'action',
    });
}

export function trackProfileOpenServiceRequest(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_open_service_request',
        eventType: 'action',
    });
}

export function trackProfileCopyCustomerId(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_copy_customer_id',
        eventType: 'action',
    });
}

export function trackProfileDeleteAccountPress(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_delete_account_press',
        eventType: 'action',
    });
}

export function trackProfileDeleteAccountStarted(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_delete_account_started',
        eventType: 'action',
    });
}

export function trackProfileDeleteAccountSuccess(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_delete_account_success',
        eventType: 'action',
    });
}

export function trackProfileDeleteAccountError(message: string): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PROFILE,
        actionName: 'profile_delete_account_error',
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackPrivacySecurityChangePhoneClick(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PRIVACY_SECURITY,
        actionName: 'privacy_security_change_phone_click',
        eventType: 'action',
    });
}

export function trackPrivacySecurityChangeEmailClick(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.PRIVACY_SECURITY,
        actionName: 'privacy_security_change_email_click',
        eventType: 'action',
    });
}
